import { useCallback, useState } from "react";

import { useEvmBackend } from "../context";
import { decodeRevertReason } from "../lib/abis";
import { isConnectionError, parseViemError } from "../lib/errors";

import type { Hex } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TxStep {
  label: string;
  status: "idle" | "pending" | "confirming" | "success" | "skipped" | "error";
  hash?: Hex;
  error?: string;
}

export interface StepDefinition {
  label: string;
  execute: () => Promise<Hex>;
  skip?: () => boolean | Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTransactionFlow(): {
  steps: TxStep[];
  currentStep: number;
  isExecuting: boolean;
  execute: (
    stepDefs: StepDefinition[],
    onSuccess?: () => void,
    onError?: (stepLabel: string, errorMessage: string) => void,
  ) => Promise<void>;
  reset: () => void;
} {
  const { httpClient } = useEvmBackend();
  const [steps, setSteps] = useState<TxStep[]>([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isExecuting, setIsExecuting] = useState(false);

  const execute = useCallback(
    async (
      stepDefs: StepDefinition[],
      onSuccess?: () => void,
      onError?: (stepLabel: string, errorMessage: string) => void,
    ) => {
      setIsExecuting(true);
      setSteps(stepDefs.map((s) => ({ label: s.label, status: "idle" })));
      setCurrentStep(0);

      for (let i = 0; i < stepDefs.length; i++) {
        setCurrentStep(i);
        const def = stepDefs[i];
        if (!def) continue;

        if (def.skip) {
          try {
            const shouldSkip = await def.skip();
            if (shouldSkip) {
              setSteps((prev) =>
                prev.map((s, idx) =>
                  idx === i ? { ...s, status: "skipped" } : s,
                ),
              );
              continue;
            }
          } catch (skipErr) {
            if (isConnectionError(skipErr)) {
              const errorMessage = parseViemError(skipErr);
              setSteps((prev) =>
                prev.map((s, idx) =>
                  idx === i
                    ? { ...s, status: "error", error: errorMessage }
                    : s,
                ),
              );
              onError?.(def.label, errorMessage);
              setIsExecuting(false);
              return;
            }
          }
        }

        setSteps((prev) =>
          prev.map((s, idx) => (idx === i ? { ...s, status: "pending" } : s)),
        );

        try {
          const hash = await def.execute();

          setSteps((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: "confirming", hash } : s,
            ),
          );

          const receipt = await httpClient.waitForTransactionReceipt({
            hash,
            timeout: 60_000,
            pollingInterval: 1_000,
          });
          if (receipt.status === "reverted") {
            let reason = "";
            try {
              const trace: { output?: Hex } =
                await httpClient.transport.request({
                  method: "debug_traceTransaction",
                  params: [hash, { tracer: "callTracer" }],
                });
              reason = decodeRevertReason(trace.output) ?? "";
            } catch {
              // debug_traceTransaction may not be available
            }
            throw new Error(
              reason
                ? `Transaction reverted: ${reason}`
                : "Transaction reverted on chain. Check the block explorer for details.",
            );
          }

          setSteps((prev) =>
            prev.map((s, idx) => (idx === i ? { ...s, status: "success" } : s)),
          );
        } catch (err) {
          const errorMessage = parseViemError(err);
          setSteps((prev) =>
            prev.map((s, idx) =>
              idx === i ? { ...s, status: "error", error: errorMessage } : s,
            ),
          );
          onError?.(def.label, errorMessage);
          setIsExecuting(false);
          return;
        }
      }

      setCurrentStep(stepDefs.length);
      setIsExecuting(false);
      onSuccess?.();
    },
    [httpClient],
  );

  const reset = useCallback(() => {
    setSteps([]);
    setCurrentStep(-1);
    setIsExecuting(false);
  }, []);

  return { steps, currentStep, isExecuting, execute, reset };
}
